import React, { useCallback, useEffect, useRef, useState } from "react";
import { useUnityContext } from "react-unity-webgl";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import VoiceRecorder from "./VoiceRecorder";
import ViewerHeader from "./ViewerHeader";
import EnvironmentMap from "./EnvironmentMap";
import Label from "./Label";
import MicAccessPopup from "./MicAccessPopup";
import StartGuideDialog from "./StartGuideDialog";
import GuidePrototypes from "./Guideprototypes";
import SessionTimelinePanel, { SessionTimelineToggleButton } from "./SessionTimelinePanel";

import ExperiencesAPI from "../services/ExperiencesAPI";
import { GAME_STATES, GameStateProvider, useGameState } from "../context/gameState";

import useWebGpuDebug from "./unityWebViewer/useWebGpuDebug";
import useUnityEventBindings from "./unityWebViewer/useUnityEventBindings";
import MobileFallback from "./unityWebViewer/MobileFallback";
import StatusOverlays from "./unityWebViewer/StatusOverlays";
import EdgeCameraControls from "./unityWebViewer/EdgeCameraControls";
import UnityCanvas from "./unityWebViewer/UnityCanvas";

const UNITY_BUILD_PREFIX = "/webplayer/10kScienceWeb_1.2.36p6_Production";
const SHOW_GUIDE_MANAGER_PANEL = true;
const SHOW_SESSION_INSIGHTS_PANEL = true;

const UnityWebViewerContent = ({ experienceId, embed, userData }) => {
  const voiceRecorderRef = useRef(null);
  const [currentElement, setCurrentElement] = useState(null);
  const [systemData, setSystemData] = useState(null);
  const [focusedElement, setFocusedElement] = useState(null);
  const [focusedPanelElementLocked, setFocusedPanelElementLocked] =
    useState(null);
  const [labelPosition, setLabelPosition] = useState(null);
  const [currentEnvironment, setCurrentEnvironment] = useState(null);
  const [systemLoaded, setSystemLoaded] = useState(false);
  const [experienceData, setExperienceData] = useState(null);
  const [layoutOverridesAll, setLayoutOverridesAll] = useState(null);
  const [layoutOverrides, setLayoutOverrides] = useState(null);
  const [cursorStyle, setCursorStyle] = useState("default");
  const focusTimeoutRef = useRef(null);
  const latestMousePositionRef = useRef({ x: 0, y: 0 });
  const [environmentsVisited, setEnvironmentsVisited] = useState(0);
  const [environmentLookup, setEnvironmentLookup] = useState({});
  const [elementLookup, setElementLookup] = useState({});
  const [localEvents, setLocalEvents] = useState([]);
  const currentElementRef = useRef(null);
  const [currentInsight, setCurrentInsight] = useState(null);
  const [instructionsVisible, setInstructionVisible] = useState(false);
  const [showControlsOverlay, setShowControlsOverlay] = useState(false);
  const [edgeHover, setEdgeHover] = useState({
    left: false,
    right: false,
    top: false,
    bottom: false,
  });
  const edgeHoverRef = useRef({
    left: false,
    right: false,
    top: false,
    bottom: false,
  });

  const [showEnvironmentLabel, setShowEnvironmentLabel] = useState(false);
  const [guideState, setGuideState] = useState(null);
  const [guideStateText, setGuideStateText] = useState(
    "Waiting for guide state...",
  );
  const [sessionId, setSessionId] = useState(null);
  const [showTimelinePanel, setShowTimelinePanel] = useState(false);
  const [highlightTimelineButton, setHighlightTimelineButton] = useState(false);

  // ✅ NEW: tracks whether VoiceRecorder is actively recording
  const [isRecording, setIsRecording] = useState(false);
  // null = not yet chosen, true = guide on, false = opted out
  const [guideEnabled, setGuideEnabled] = useState(null);

  const environmentLabelTimeoutRef = useRef(null);
  const { currentGameState, setGameState, isCurrentGameState } = useGameState();

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const overlayWidth = isPhone ? "92vw" : isTablet ? "70vw" : "540px";
  const overlayPadding = isPhone ? "6px 12px" : "8px 16px";
  const topMessageOffset = isPhone ? "70px" : "90px";
  const envLabelBottom = isPhone ? "100px" : "150px";
  const edgeControlWidth = isPhone ? 48 : 60;
  const edgeControlHeight = isPhone ? 46 : 54;
  const focusedPanelWidth = isPhone ? "100%" : "50%";
  const rightPanelInset =
    "var(--panel-horizontal-offset, var(--panel-left-offset, 20px))";
  const rightPanelRight = `calc(env(safe-area-inset-right, 0px) + ${rightPanelInset})`;
  const rightPanelBottom = `calc(env(safe-area-inset-bottom, 0px) + ${rightPanelInset})`;
  const rightPanelTopViewport = `calc(env(safe-area-inset-top, 0px) + ${rightPanelInset})`;
  const rightPanelWidth = "var(--panel-width-standard, 250px)";
  const rightPanelMaxWidth = `calc(100vw - ${rightPanelInset} - ${rightPanelInset})`;

  useWebGpuDebug();

  const { unityProvider, sendMessage, addEventListener, removeEventListener } =
    useUnityContext({
      loaderUrl: `${UNITY_BUILD_PREFIX}.loader.js`,
      dataUrl: `${UNITY_BUILD_PREFIX}.data`,
      frameworkUrl: `${UNITY_BUILD_PREFIX}.framework.js`,
      codeUrl: `${UNITY_BUILD_PREFIX}.wasm`,
    });

  const playerOffset = 0;
  const dragStart = useRef(null);

  const sendMessageToUnity = useCallback(
    (gameObject, methodName, value) => {
      sendMessage(gameObject, methodName, value);
    },
    [sendMessage],
  );

  const handleElementSelected = useCallback((elementData) => {
    const parsedElement =
      typeof elementData === "string" ? JSON.parse(elementData) : elementData;
    setCurrentElement(parsedElement);
    currentElementRef.current = parsedElement;

    if (parsedElement?.id) {
      const elementName =
        parsedElement.labelData?.displayName ||
        parsedElement.labelData?.name ||
        parsedElement.name ||
        parsedElement.id;

      setElementLookup((prev) => ({
        ...prev,
        [parsedElement.id]: { name: elementName },
      }));
    }
  }, []);

  const handleInsightStarted = useCallback((insightId) => {
    ExperiencesAPI.get(`insights/${insightId}`)
      .then((response) => {
        setCurrentInsight(response.data.data);
      })
      .catch((error) => {
        console.error("Failed to fetch insight data", error);
      });
  }, []);

  const handleGameStateChanged = useCallback(
    (gameStateName) => {
      console.log("🎮 Game state changed:", gameStateName);
      setGameState(gameStateName);

      if (
        gameStateName === "ViewModeElementFocused" &&
        currentElementRef.current?.id
      ) {
        const elem = currentElementRef.current;
        const elementName =
          elem.labelData?.displayName ||
          elem.labelData?.name ||
          elem.name ||
          elem.id;

        setLocalEvents((prev) => {
          const lastElemEvent = [...prev]
            .reverse()
            .find((e) => e.pk?.startsWith("ElementExpanded#"));
          if (lastElemEvent && lastElemEvent.value === elem.id) {
            return prev;
          }
          return [
            ...prev,
            {
              pk: `ElementExpanded#${elem.id}`,
              sk: new Date().toISOString(),
              value: elem.id,
              elementName: elementName,
            },
          ];
        });
      }
    },
    [setGameState],
  );

  const handleAssessmentComplete = useCallback((assessmentId) => {
    setTimeout(() => {
      ExperiencesAPI.get(`assessments/${assessmentId}`).catch((error) => {
        console.error("Failed to fetch assessment data", error);
      });
    }, 3000);
  }, []);

  const handleInstructionVisibilityChanged = useCallback((visible) => {
    setInstructionVisible(visible.toLowerCase() === "true");
  }, []);

  const handleEnvironmentChanged = useCallback((environmentData) => {
    setEnvironmentsVisited((prev) => {
      const next = prev + 1;
      setShowControlsOverlay(next === 3);
      return next;
    });

    try {
      const parsedEnvironment =
        typeof environmentData === "string"
          ? JSON.parse(environmentData)
          : environmentData;
      setFocusedElement(null);
      setCurrentEnvironment(parsedEnvironment);

      if (parsedEnvironment?.id) {
        setEnvironmentLookup((prev) => ({
          ...prev,
          [parsedEnvironment.id]: {
            name:
              parsedEnvironment.environmentName ||
              parsedEnvironment.name ||
              parsedEnvironment.id,
          },
        }));
      }

      if (environmentLabelTimeoutRef.current) {
        clearTimeout(environmentLabelTimeoutRef.current);
      }

      setShowEnvironmentLabel(true);

      environmentLabelTimeoutRef.current = setTimeout(() => {
        setShowEnvironmentLabel(false);
        environmentLabelTimeoutRef.current = null;
      }, 2500);
    } catch (error) {
      console.error("Failed to parse environmentData", error);
    }
  }, []);

  // ✅ UPDATED: also sets isRecording = true so the orb card knows to show the inline UI
  const handleRecordingStarted = useCallback((instanceId) => {
    setIsRecording(true);
    if (voiceRecorderRef.current) {
      voiceRecorderRef.current.startRecording(instanceId);
    }
  }, []);

  const handleGuideStateChanged = useCallback((guideStateData) => {
    if (guideStateData == null) {
      setGuideState(null);
      setGuideStateText("Guide state payload was empty.");
      return;
    }

    if (typeof guideStateData === "string") {
      try {
        const parsed = JSON.parse(guideStateData);
        setGuideState(parsed);
        setGuideStateText(parsed?.data ?? "");
        return;
      } catch {
        setGuideState({ guideState: "UNKNOWN", data: guideStateData });
        setGuideStateText(guideStateData);
        return;
      }
    }

    if (typeof guideStateData === "object") {
      setGuideState(guideStateData);
      setGuideStateText(guideStateData?.data ?? "");
      return;
    }

    setGuideState({ guideState: "UNKNOWN", data: String(guideStateData) });
    setGuideStateText(String(guideStateData));
  }, []);

  const handleSessionStarted = useCallback((newSessionId) => {
    if (typeof newSessionId !== "string" || newSessionId.trim() === "") {
      console.warn("sessionStarted received invalid session id", newSessionId);
      return;
    }

    console.log("SESSION STARTED", newSessionId);
    setSessionId(newSessionId);
  }, []);

  useEffect(() => {
    const sessionStartedPattern = /SessionStarted:\s*([0-9a-fA-F-]{36})/;
    const originalConsoleLog = console.log;

    const patchedConsoleLog = (...args) => {
      if (typeof args[0] === "string") {
        const match = args[0].match(sessionStartedPattern);
        if (match?.[1]) {
          setSessionId((currentSessionId) => currentSessionId || match[1]);
          if (console.log === patchedConsoleLog) {
            console.log = originalConsoleLog;
          }
        }
      }

      originalConsoleLog(...args);
    };

    console.log = patchedConsoleLog;

    return () => {
      if (console.log === patchedConsoleLog) {
        console.log = originalConsoleLog;
      }
    };
  }, []);

  const handleUploadAudio = useCallback((presignedUrl) => {
    if (voiceRecorderRef.current) {
      voiceRecorderRef.current.uploadFileToS3(presignedUrl);
    }
  }, []);

  const handleReadyToLoadExperience = useCallback(() => {
    console.log("USER DATA", userData)
    sendMessageToUnity("EventDispatcher", "SetUserData", JSON.stringify(userData));
    sendMessageToUnity("EventDispatcher", "LoadExperience", experienceId);
  }, [experienceId, sendMessageToUnity]);

  const handleSystemLoaded = useCallback((systemDataValue) => {
    const parsedSystem =
      typeof systemDataValue === "string"
        ? JSON.parse(systemDataValue)
        : systemDataValue;
    setSystemData(parsedSystem);
  }, []);

  const handleSystemFullyLoaded = useCallback(() => {
    setSystemLoaded(true);
  }, []);

  const handleElementDeselected = useCallback(() => {
    setCurrentElement(null);
  }, []);

  useUnityEventBindings({
    addEventListener,
    removeEventListener,
    onElementSelected: handleElementSelected,
    onElementDeselected: handleElementDeselected,
    onEnvironmentChanged: handleEnvironmentChanged,
    onRecordingStarted: handleRecordingStarted,
    onUploadAudioFile: handleUploadAudio,
    onReadyToLoadExperience: handleReadyToLoadExperience,
    onSystemLoaded: handleSystemLoaded,
    onSystemFullyLoaded: handleSystemFullyLoaded,
    onAssessmentComplete: handleAssessmentComplete,
    onInstructionVisibilityChanged: handleInstructionVisibilityChanged,
    onInsightStarted: handleInsightStarted,
    onGameStateChanged: handleGameStateChanged,
    onGuideStateChanged: handleGuideStateChanged,
    onSessionStarted: handleSessionStarted,
  });

  useEffect(() => {
    if (experienceId) {
      ExperiencesAPI.get(`experiences/${experienceId}`).then((response) => {
        setExperienceData(response.data);
      });
    }
  }, [experienceId]);

  useEffect(() => {
    if (!guideStateText) {
      setHighlightTimelineButton(false);
      return;
    }

    const text = guideStateText.toLowerCase();
    const triggerPhrases = [
      "dashboard",
      "top right",
      "top-right",
      "timeline",
      "student responses",
      "view your responses",
      "click the icon",
      "click on the icon",
    ];

    const shouldHighlight = triggerPhrases.some((phrase) =>
      text.includes(phrase),
    );
    setHighlightTimelineButton(shouldHighlight);
  }, [guideStateText]);

  useEffect(() => {
    const loadOverrides = async () => {
      try {
        const response = await fetch("/environmentLayout.json", {
          cache: "no-cache",
        });
        if (!response.ok) return;
        const data = await response.json();
        setLayoutOverridesAll(data);
      } catch (error) {
        console.warn("Failed to load environment layout overrides", error);
      }
    };

    loadOverrides();
  }, []);

  useEffect(() => {
    if (!layoutOverridesAll || !systemData?.id) {
      setLayoutOverrides(null);
      return;
    }

    const overridesForSystem = layoutOverridesAll[systemData.id];
    setLayoutOverrides(
      overridesForSystem && typeof overridesForSystem === "object"
        ? overridesForSystem
        : null,
    );
  }, [layoutOverridesAll, systemData]);

  useEffect(() => {
    const rootStyles = getComputedStyle(document.documentElement);
    const desktopHoverZone = parseFloat(
      rootStyles.getPropertyValue("--edge-hover-zone-desktop"),
    );
    const phoneHoverZone = parseFloat(
      rootStyles.getPropertyValue("--edge-hover-zone-phone"),
    );
    const resolvedDesktopHoverZone =
      Number.isFinite(desktopHoverZone) && desktopHoverZone > 0
        ? desktopHoverZone
        : 150;
    const resolvedPhoneHoverZone =
      Number.isFinite(phoneHoverZone) && phoneHoverZone > 0
        ? phoneHoverZone
        : 120;

    const clearEdgeHover = () => {
      const clearedHover = {
        left: false,
        right: false,
        top: false,
        bottom: false,
      };
      const prevHover = edgeHoverRef.current;
      if (
        prevHover.left ||
        prevHover.right ||
        prevHover.top ||
        prevHover.bottom
      ) {
        edgeHoverRef.current = clearedHover;
        setEdgeHover(clearedHover);
      }
    };

    const handleMouseMove = (event) => {
      const unityContainer = document.querySelector(".content");
      if (!unityContainer) return;

      const rect = unityContainer.getBoundingClientRect();
      const edgeHoverWidth = isPhone
        ? resolvedPhoneHoverZone
        : resolvedDesktopHoverZone;
      const edgeHoverHeight = isPhone
        ? resolvedPhoneHoverZone
        : resolvedDesktopHoverZone;

      latestMousePositionRef.current = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };

      const x = latestMousePositionRef.current.x;
      const y = latestMousePositionRef.current.y;
      const withinViewport =
        x >= 0 && x <= rect.width && y >= 0 && y <= rect.height;

      const nextHover = {
        left: withinViewport && x <= edgeHoverWidth,
        right: withinViewport && x >= rect.width - edgeHoverWidth,
        top: withinViewport && y <= edgeHoverHeight,
        bottom: withinViewport && y >= rect.height - edgeHoverHeight,
      };

      const prevHover = edgeHoverRef.current;
      if (
        prevHover.left !== nextHover.left ||
        prevHover.right !== nextHover.right ||
        prevHover.top !== nextHover.top ||
        prevHover.bottom !== nextHover.bottom
      ) {
        edgeHoverRef.current = nextHover;
        setEdgeHover(nextHover);
      }
    };

    const handleWindowMouseOut = (event) => {
      if (!event.relatedTarget && !event.toElement) {
        clearEdgeHover();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        clearEdgeHover();
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseout", handleWindowMouseOut);
    window.addEventListener("blur", clearEdgeHover);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseout", handleWindowMouseOut);
      window.removeEventListener("blur", clearEdgeHover);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isPhone]);

  useEffect(() => {
    const handleMouseMove = () => {
      if (currentElement && currentElement.targetEnvironmentID) {
        setCursorStyle("zoom-in");
      } else if (currentElement) {
        setCursorStyle("pointer");
      } else {
        setCursorStyle("move");
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [currentElement]);

  const handleMouseDown = (event) => {
    dragStart.current = { x: event.clientX, y: event.clientY };
    setFocusedElement(null);
    setLabelPosition(null);
  };

  const handleMouseUp = (event) => {
    const threshold = 5;
    const dx = event.clientX - dragStart.current.x;
    const dy = event.clientY - dragStart.current.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance <= threshold) {
      if (currentElement) {
        setFocusedElement(currentElement);
      } else {
        setFocusedElement(null);
      }
      const { offsetX, offsetY } = event.nativeEvent;
      setLabelPosition({ x: offsetX + playerOffset, y: offsetY });
    }
  };

  useEffect(() => {
    if (!currentElement) {
      clearTimeout(focusTimeoutRef.current);
      setFocusedElement(null);
      setLabelPosition(null);
      return;
    }

    clearTimeout(focusTimeoutRef.current);
    focusTimeoutRef.current = setTimeout(() => {
      setFocusedElement(currentElement);
      const { x, y } = latestMousePositionRef.current;
      setLabelPosition({ x, y });
    }, 300);

    return () => clearTimeout(focusTimeoutRef.current);
  }, [currentElement]);

  // ✅ UPDATED: clears isRecording in addition to notifying Unity
  const doneRecording = () => {
    setIsRecording(false);
    sendMessageToUnity("EventDispatcher", "RecordingStopped");
  };

  const stopRecordingFromGuide = useCallback(() => {
    if (voiceRecorderRef.current?.stopRecording) {
      voiceRecorderRef.current.stopRecording();
    }
  }, []);

  const assessmentComplete = () => {
    sendMessageToUnity("EventDispatcher", "CompleteAssessment");
  };

  const changeEnvironment = (environmentId) => {
    sendMessageToUnity("EventDispatcher", "ChangeEnvironment", environmentId);
  };

  const startGuide = () => {
    sendMessageToUnity("EventDispatcher", "LoadGuide", experienceData.guideId);
  };

  const rotateCamera = (direction) => {
    sendMessageToUnity("EventDispatcher", "RotateCamera", direction);
  };

  const setUserData = (userData) => {
    sendMessageToUnity("EventDispatcher", "SetUserData", userData);
  };

  const getDisplayName = (elementData) => {
    return !elementData.targetEnvironmentID
      ? `${elementData.labelData.displayName}`
      : `${elementData.labelData.displayName} 🔍`;
  };

  const getSubtitle = (elementData) => {
    return elementData.targetEnvironmentID && environmentsVisited < 6
      ? "Scroll or pinch to zoom in."
      : null;
  };

  const isMobile =
    typeof window !== "undefined" &&
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );

  const showEnvironmentMap = Boolean(
    systemData &&
    isCurrentGameState([
      GAME_STATES.ViewMode,
      GAME_STATES.ViewModeElementSelected,
      GAME_STATES.ZoomingIn,
      GAME_STATES.ZoomingOut,
    ]),
  );
  const showElementLabelPanel = Boolean(
    focusedElement &&
    labelPosition &&
    isCurrentGameState([GAME_STATES.ViewModeElementSelected]),
  );
  const isElementFocusedGameState = isCurrentGameState([
    GAME_STATES.ViewModeElementFocused,
  ]);
  const focusedPanelElementCandidate = focusedElement || currentElement;
  const showEdgeCameraControls = isCurrentGameState([
    GAME_STATES.ViewMode,
    GAME_STATES.ViewModeElementSelected,
  ]);
  const showFocusedElementPanel = Boolean(
    focusedPanelElementLocked && isElementFocusedGameState,
  );

  useEffect(() => {
    if (!isElementFocusedGameState) {
      setFocusedPanelElementLocked(null);
      return;
    }
    if (!focusedPanelElementLocked && focusedPanelElementCandidate) {
      setFocusedPanelElementLocked(focusedPanelElementCandidate);
      setShowTimelinePanel(false);
    }
  }, [
    focusedPanelElementLocked,
    focusedPanelElementCandidate,
    isElementFocusedGameState,
  ]);

  if (isMobile) {
    return <MobileFallback isPhone={isPhone} />;
  }

  return (
    <GameStateProvider>
      <div
        className="content"
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        <StatusOverlays
          showEnvironmentLabel={showEnvironmentLabel}
          envLabelBottom={envLabelBottom}
          playerOffset={playerOffset}
          overlayPadding={overlayPadding}
          overlayWidth={overlayWidth}
          currentEnvironment={currentEnvironment}
          instructionsVisible={instructionsVisible}
          topMessageOffset={topMessageOffset}
          currentInsight={currentInsight}
          showControlsOverlay={showControlsOverlay}
          isPhone={isPhone}
          currentGameState={currentGameState}
        />

        <UnityCanvas
          unityProvider={unityProvider}
          cursorStyle={cursorStyle}
          playerOffset={playerOffset}
        />

        {showEdgeCameraControls && (
          <EdgeCameraControls
            edgeHover={edgeHover}
            edgeControlWidth={edgeControlWidth}
            edgeControlHeight={edgeControlHeight}
            rotateCamera={rotateCamera}
          />
        )}

        {!embed && <ViewerHeader system={systemData} />}

        {/* Session Timeline Toggle Button - Top Right */}
        {SHOW_SESSION_INSIGHTS_PANEL && systemLoaded && (
          <Box
            sx={{
              position: "absolute",
              top: rightPanelTopViewport,
              right: rightPanelRight,
              zIndex: 1002,
              pointerEvents: "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 1.5,
            }}
          >
            <SessionTimelineToggleButton
              isOpen={showTimelinePanel}
              onClick={() => setShowTimelinePanel((prev) => !prev)}
              highlight={highlightTimelineButton}
            />

            {showTimelinePanel && (
              <Box
                sx={{
                  width: rightPanelWidth,
                  maxWidth: rightPanelMaxWidth,
                  boxSizing: "border-box",
                  pointerEvents: "auto",
                }}
              >
                <SessionTimelinePanel
                  sessionId={sessionId}
                  environmentLookup={environmentLookup}
                  elementLookup={elementLookup}
                  localEvents={localEvents}
                  isOpen={showTimelinePanel}
                  onClose={() => setShowTimelinePanel(false)}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Right-side stack: Guide + Recorder (bottom right) */}
        <Box
          sx={{
            position: "absolute",
            right: rightPanelRight,
            bottom: rightPanelBottom,
            width: rightPanelWidth,
            maxWidth: rightPanelMaxWidth,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            zIndex: 1000,
            pointerEvents: "none",
          }}
        >
          {SHOW_GUIDE_MANAGER_PANEL &&
            systemLoaded &&
            guideEnabled === true &&
            !showFocusedElementPanel && (
              <GuidePrototypes
                guideState={guideState}
                guideStateText={guideStateText}
                startGuide={startGuide}
                isRecording={isRecording}
                onStopRecording={stopRecordingFromGuide}
              />
            )}

          <VoiceRecorder
            doneRecording={doneRecording}
            assessmentComplete={assessmentComplete}
            ref={voiceRecorderRef}
            stacked
            hideUi
          />
        </Box>

        <Box
          component="div"
          sx={{
            position: "absolute",
            transform: showElementLabelPanel
              ? "translateY(0px)"
              : "translateY(8px)",
            opacity: showElementLabelPanel ? 1 : 0,
            transition: "opacity 300ms ease, transform 300ms ease",
            pointerEvents: "none",
            zIndex: 10,
            visibility: showElementLabelPanel ? "visible" : "hidden",
          }}
        >
          {showElementLabelPanel && (
            <Label
              key={focusedElement.id}
              labelPosition={labelPosition}
              displayName={getDisplayName(focusedElement)}
              subtitle={getSubtitle(focusedElement)}
              alignment="auto"
              offsetLeft={0}
            />
          )}
        </Box>

        {showFocusedElementPanel && (
          <>
            <Box
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                width: focusedPanelWidth,
                height: "100%",
                background: isPhone
                  ? "linear-gradient(180deg, rgba(0, 0, 0, 0.82) 0%, rgba(0, 0, 0, 0.56) 68%, rgba(0, 0, 0, 0) 100%)"
                  : "linear-gradient(270deg, rgba(0, 0, 0, 0.82) 0%, rgba(0, 0, 0, 0.62) 56%, rgba(0, 0, 0, 0) 100%)",
                pointerEvents: "none",
                zIndex: 15,
              }}
            />

            <Box
              sx={{
                position: "absolute",
                top: "var(--app-header-height, 0px)",
                right: 0,
                width: focusedPanelWidth,
                height: "calc(100% - var(--app-header-height, 0px))",
                color: "#fff",
                pointerEvents: "none",
                zIndex: 16,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  px: isPhone ? 2.5 : 4,
                  width: isPhone ? "100%" : "60%",
                  mx: "auto",
                  textAlign: "left",
                  transform: "none",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.72,
                    letterSpacing: "0.11em",
                    textTransform: "uppercase",
                  }}
                >
                  ELEMENT
                </Typography>
                <Typography
                  variant={isPhone ? "h5" : "h4"}
                  sx={{
                    mt: 0.5,
                    fontSize: isPhone ? "1.25rem" : "1.55rem",
                    lineHeight: 1.2,
                  }}
                >
                  {getDisplayName(focusedPanelElementLocked)}
                </Typography>
                {focusedPanelElementLocked?.labelData?.description && (
                  <Typography
                    variant="body1"
                    sx={{
                      mt: 1.25,
                      textAlign: "left",
                      lineHeight: 1.45,
                      opacity: 0.96,
                    }}
                  >
                    {focusedPanelElementLocked.labelData.description}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box
              component="div"
              aria-hidden="true"
              sx={{
                position: "absolute",
                top: "calc(var(--app-header-height, 0px) + 14px)",
                right: "max(14px, env(safe-area-inset-right, 0px))",
                width: isPhone ? 44 : 54,
                height: isPhone ? 44 : 54,
                borderRadius: "999px",
                backgroundColor: "rgba(9, 12, 18, 0.62)",
                color: "#fff",
                fontSize: isPhone ? "1.9rem" : "2.2rem",
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 17,
                pointerEvents: "none",
                boxShadow: "0 14px 34px rgba(0, 0, 0, 0.34)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                transition:
                  "background-color 160ms ease, border-color 160ms ease, transform 160ms ease",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.58)",
                  transform: "scale(1.05)",
                },
              }}
            >
              ×
            </Box>
          </>
        )}

        {showEnvironmentMap && (
          <EnvironmentMap
            systemData={systemData}
            currentEnvironmentId={currentEnvironment?.id}
            changeEnvironment={changeEnvironment}
            layoutOverrides={layoutOverrides}
          />
        )}

        {systemLoaded && guideEnabled === null && (
          <StartGuideDialog
            onStartGuide={() => {
              setGuideEnabled(true);
              startGuide();
            }}
            onSkip={() => setGuideEnabled(false)}
          />
        )}
        <MicAccessPopup />
      </div>
    </GameStateProvider>
  );
};


const UnityWebViewer = (props) => {
  return (
    <GameStateProvider>
      <UnityWebViewerContent {...props} />
    </GameStateProvider>
  );
};



export default UnityWebViewer;
